class NuDropdown extends HTMLElement {
    #select;
    #options = [];
    #selectedOption = null;
    #label;
    #required;
    #dropdownArrow = `<svg xmlns="http://www.w3.org/2000/svg" width="11.414" height="7.121" viewBox="0 0 11.414 7.121"><path id="Path_7" data-name="Path 7" d="M-16361.465,9642l5,5,5-5" transform="translate(16362.172 -9641.293)" fill="none" stroke="#000" stroke-width="2"/></svg>`;

    constructor() {
        super();

        // Store custom attributes before replacing content
        const customAttributes = {};
        for (const attr of this.attributes) {
            customAttributes[attr.name] = attr.value;
        }

        this.#label = this.querySelector('label');
        this.#required = this.querySelector('span.required');

        // Handle both select and text input cases
        this.#select = this.querySelector('select');
        const textInput = this.querySelector('input[type="text"]');

        if (this.#select) {
            // Handle select dropdown
            this.#options = Array.from(this.#select.options).map(option => ({
                value: option.value,
                text: option.textContent,
                selected: option.selected
            }));
            this.#selectedOption = this.#options.find(opt => opt.selected) || this.#options[0];
        } else if (textInput) {
            // Handle text input (like date range)
            this.#options = [{
                value: '',
                text: textInput.value,
                selected: true
            }];
            this.#selectedOption = this.#options[0];
        }

        const labelText = this.#label.childNodes[0].textContent.trim();

        const hxTrigger = this.#select ? this.#select.getAttribute('hx-trigger') : '';

        // Create wrapper div with preserved attributes
        const wrapper = document.createElement('div');
        for (const [name, value] of Object.entries(customAttributes)) {
            wrapper.setAttribute(name, value);
        }

        wrapper.innerHTML = `
            <div class="dropdown">
                <button class="dropdown-button">
                    <span class="dropdown-label">${labelText} ${this.#required ? '<span class="required">*</span>' : ''}</span>
                    <span class="dropdown-value">${this.#selectedOption?.text || 'Select'}</span>
                    ${this.#dropdownArrow}
                </button>
                ${this.#select ? `
                    <div class="dropdown-content">
                        ${this.#options.map(option => `
                            <div class="dropdown-item" data-value="${option.value}">${option.text}</div>
                        `).join('')}
                    </div>
                    <select name="${this.#select.name}" ${hxTrigger ? `hx-trigger="${hxTrigger}"` : ''} hidden>
                        ${this.#select.innerHTML}
                    </select>
                ` : ''}
            </div>
        `;

        // Preserve any additional inputs (like hidden inputs for date range)
        const additionalInputs = Array.from(this.querySelectorAll('input[type="hidden"]'));
        additionalInputs.forEach(input => wrapper.appendChild(input.cloneNode(true)));

        // Replace content while preserving attributes
        this.replaceChildren(...wrapper.childNodes);
    }

    connectedCallback() {
        this.querySelector('.dropdown-button').addEventListener('click', (e) => {
            e.stopPropagation();
            e.preventDefault(); // Prevent HTMX transition
            this.toggleDropdown();
        });

        this.querySelectorAll('.dropdown-item').forEach(item => {
            item.addEventListener('click', (e) => {
                e.stopPropagation();
                this.selectItem(e);
            });
        });

        // Click Outside Dropdown
        document.addEventListener('click', (e) => {
            if (!this.contains(e.target)) {
                this.closeDropdown();
            }
        });

        // Prevent HTMX transition on form submit
        this.closest('form').addEventListener('submit', (e) => {
            if (this.querySelector('.dropdown').classList.contains('show')) {
                e.stopPropagation();
            }
        });
    }

    closeDropdown() {
        this.querySelector('.dropdown').classList.remove('show');
    }

    toggleDropdown() {
        // Close all other dropdowns first
        document.querySelectorAll('nu-dropdown .dropdown.show')
            .forEach(dropdown => {
                if (!this.contains(dropdown)) {
                    dropdown.classList.remove('show');
                }
            });

        this.querySelector('.dropdown').classList.toggle('show');
    }

    selectItem(event) {
        const value = event.target.getAttribute('data-value');
        this.querySelector(`option[value="${value}"]`).selected = true;
        this.#selectedOption = this.#options.find(opt => opt.value === value);

        // Update the value span instead of button text
        this.querySelector('.dropdown-value').textContent = this.#selectedOption.text;

        // Dispatch change event to trigger HTMX
        const changeEvent = new Event('change', { bubbles: true });
        this.querySelector('select').dispatchEvent(changeEvent);

        const form = this.closest('form');

        if (form && form.classList.contains('htmx-enabled')) {
            form.dispatchEvent(changeEvent);
        }

        this.closeDropdown();
    }
}

customElements.define('nu-dropdown', NuDropdown);
